import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { 
  Truck, Package, ShoppingCart, Check, Download, ExternalLink, AlertTriangle, 
  ArrowLeft, FileText, BookOpen, Calendar, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { getBookCover } from '@/lib/assets';

const orderSteps = ['Order Placed', 'Processing', 'Dispatched', 'In Transit', 'Delivered', 'Acknowledged'];

// Sample order data
const orderData = {
  id: 'PO-OPS-CDS-C1-2028',
  school: "Orchid Primary",
  cycle: 'CDS Cycle 1 2028',
  placedDate: '12 Sep 2026',
  totalBooks: 91,
  totalAmount: 2847.50,
  budget: 3000,
  status: 'in_progress',
  deliveryPeriod: 'Jan – May 2028',
  batches: [
    { id: 1, month: 'Jan', books: 20, status: 'delivered', deliveredDate: '18 Jan 2028', acknowledgedDate: '18 Jan 2028' },
    { id: 2, month: 'Feb', books: 20, status: 'in_transit', tracking: 'SP-TRK-20280205-OPS-001', eta: '10 Feb 2028' },
    { id: 3, month: 'Mar', books: 18, status: 'scheduled', dispatchDate: '5 Mar 2028' },
    { id: 4, month: 'Apr', books: 18, status: 'scheduled', dispatchDate: '5 Apr 2028' },
    { id: 5, month: 'May', books: 15, status: 'scheduled', dispatchDate: '5 May 2028' },
  ],
  items: [
    { id: 1, title: 'The Wonders of Coding', author: 'Sarah Chen', qty: 1, price: 18.50, category: 'NF EL', supplier: 'OpenTrolley', batch: 1, status: 'delivered' },
    { id: 2, title: 'Climate Change & You', author: 'David Atkins', qty: 2, price: 14.20, category: 'NF EL', supplier: 'OpenTrolley', batch: 1, status: 'delivered' },
    { id: 3, title: 'Between Two Worlds', author: 'Amelia Tan', qty: 2, price: 16.80, category: 'Fiction EL', supplier: 'Times Books', batch: 1, status: 'delivered' },
    { id: 4, title: 'The Last Library', author: 'Priya Nair', qty: 1, price: 15.90, category: 'Fiction EL', supplier: 'Times Books', batch: 2, status: 'in_transit' },
    { id: 5, title: 'Code Red', author: 'Marcus Wu', qty: 1, price: 17.20, category: 'Fiction EL', supplier: 'MPH', batch: 2, status: 'in_transit' },
    { id: 6, title: 'Singapore Stories Vol 3', author: 'Various', qty: 1, price: 22.50, category: 'NF EL', supplier: 'OpenTrolley', batch: 2, status: 'in_transit' },
    { id: 7, title: 'Daughters of Singapore', author: 'Mei Lin Goh', qty: 1, price: 16.50, category: 'Fiction EL', supplier: 'Epigram', batch: 3, status: 'scheduled' },
    { id: 8, title: 'Sound of Silence', author: 'Ravi Menon', qty: 1, price: 15.60, category: 'Fiction EL', supplier: 'Times Books', batch: 3, status: 'scheduled' },
    { id: 9, title: 'Semangat!', author: 'Hanna Abdullah', qty: 1, price: 16.80, category: 'MTL-ML', supplier: 'Grassroots', batch: 4, status: 'scheduled' },
    { id: 10, title: 'The Memory Keeper', author: 'Siti Rahimah', qty: 1, price: 16.80, category: 'Fiction EL', supplier: 'Penguin', batch: 5, status: 'scheduled' },
  ]
};

export default function SchoolOrderDetails() {
  const { orderId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');

  const handleAcknowledge = () => {
    toast.success('Delivery acknowledged successfully');
  };

  const handleDownloadPO = () => {
    toast.success('PO document downloaded');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'delivered':
        return <Badge className="bg-emerald-50 text-emerald-700">✓ Delivered</Badge>;
      case 'in_transit':
        return <Badge className="bg-blue-50 text-blue-700">🚚 In Transit</Badge>;
      case 'scheduled':
        return <Badge variant="outline">📅 Scheduled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/school/orders">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
              <FileText className="w-7 h-7 text-[#1E4D35]" />
              Order Details
            </h1>
            <p className="text-slate-500 mt-1">{orderData.id} | {orderData.cycle}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDownloadPO}>
            <Download className="w-4 h-4 mr-2" />
            Download PO PDF
          </Button>
        </div>
      </div>

      {/* Order Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-8 h-8 text-[#1E4D35]" />
              <div>
                <p className="text-2xl font-bold">{orderData.totalBooks}</p>
                <p className="text-sm text-slate-500">Total Books</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">${orderData.totalAmount.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Order Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold">{orderData.placedDate}</p>
                <p className="text-sm text-slate-500">Order Date</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Truck className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">2/5</p>
                <p className="text-sm text-slate-500">Batches Received</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Check className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-700">40</p>
                <p className="text-sm text-emerald-600">Books Received</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Order Overview</TabsTrigger>
          <TabsTrigger value="items">All Items ({orderData.items.length})</TabsTrigger>
          <TabsTrigger value="batches">Delivery Batches ({orderData.batches.length})</TabsTrigger>
          <TabsTrigger value="history">Order History</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Overall Delivery Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span>Books Delivered</span>
                  <span className="font-semibold">40 of 91 (44%)</span>
                </div>
                <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: '44%' }} />
                </div>
              </div>

              {/* Batch Timeline */}
              <div className="flex items-center justify-between mt-6">
                {orderData.batches.map((batch, idx) => (
                  <div key={batch.id} className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      batch.status === 'delivered' ? 'bg-emerald-500 text-white' :
                      batch.status === 'in_transit' ? 'bg-blue-500 text-white animate-pulse' :
                      'bg-slate-200'
                    }`}>
                      {batch.status === 'delivered' ? <Check className="w-5 h-5" /> :
                       batch.status === 'in_transit' ? <Truck className="w-5 h-5" /> :
                       <span className="text-sm">{batch.id}</span>}
                    </div>
                    <span className="text-xs mt-2 font-medium">Batch {batch.id}</span>
                    <span className="text-xs text-slate-500">{batch.month}</span>
                    <span className="text-xs text-slate-500">{batch.books} books</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Current Action */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <p className="font-medium text-amber-800">Action Required: Batch 2 has arrived!</p>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                20 books are ready for acknowledgement. Please verify receipt by 15 Feb 2028.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Track Shipment
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleAcknowledge}>
                  <Check className="w-4 h-4 mr-1" />
                  Acknowledge Receipt
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Items Tab */}
        <TabsContent value="items" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Order Line Items</CardTitle>
              <CardDescription>All books in this order</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Cover</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Batch</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orderData.items.map((item) => (
                    <TableRow key={item.id} className="table-row-striped">
                      <TableCell>
                        <img 
                          src={getBookCover(item.title)} 
                          alt={item.title}
                          className="w-10 h-14 object-cover rounded"
                        />
                      </TableCell>
                      <TableCell className="font-medium">{item.title}</TableCell>
                      <TableCell>{item.author}</TableCell>
                      <TableCell><Badge variant="outline">{item.category}</Badge></TableCell>
                      <TableCell>{item.supplier}</TableCell>
                      <TableCell>{item.qty}</TableCell>
                      <TableCell>${(item.price * item.qty).toFixed(2)}</TableCell>
                      <TableCell>Batch {item.batch}</TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Batches Tab */}
        <TabsContent value="batches" className="mt-6">
          <div className="space-y-4">
            {orderData.batches.map((batch) => (
              <Card key={batch.id} className={`${
                batch.status === 'in_transit' ? 'border-blue-200 bg-blue-50/30' :
                batch.status === 'delivered' ? 'bg-slate-50' : ''
              }`}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        batch.status === 'delivered' ? 'bg-emerald-500 text-white' :
                        batch.status === 'in_transit' ? 'bg-blue-500 text-white' :
                        'bg-slate-200'
                      }`}>
                        {batch.status === 'delivered' ? <Check className="w-5 h-5" /> :
                         batch.status === 'in_transit' ? <Truck className="w-5 h-5" /> :
                         <Package className="w-5 h-5" />}
                      </div>
                      <div>
                        <h4 className="font-semibold">Batch {batch.id} — {batch.month} 2028</h4>
                        <p className="text-sm text-slate-500">{batch.books} books</p>
                        {batch.tracking && (
                          <p className="text-xs text-slate-500 font-mono">{batch.tracking}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(batch.status)}
                      {batch.deliveredDate && (
                        <p className="text-xs text-slate-500 mt-1">Delivered: {batch.deliveredDate}</p>
                      )}
                      {batch.eta && (
                        <p className="text-xs text-blue-600 mt-1">ETA: {batch.eta}</p>
                      )}
                      {batch.dispatchDate && batch.status === 'scheduled' && (
                        <p className="text-xs text-slate-500 mt-1">Dispatch: {batch.dispatchDate}</p>
                      )}
                    </div>
                  </div>
                  {batch.status === 'in_transit' && (
                    <div className="mt-4 flex gap-2">
                      <Button variant="outline" size="sm">
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Track on SingPost
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleAcknowledge}>
                        <Check className="w-4 h-4 mr-1" />
                        Acknowledge Receipt
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Order History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-4 items-start">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2" />
                  <div>
                    <p className="font-medium">Batch 1 Acknowledged</p>
                    <p className="text-sm text-slate-500">18 Jan 2028, 10:23am by Mrs Tan Mei Ling</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2" />
                  <div>
                    <p className="font-medium">Batch 1 Delivered</p>
                    <p className="text-sm text-slate-500">18 Jan 2028, 9:15am</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                  <div>
                    <p className="font-medium">Batch 2 Dispatched</p>
                    <p className="text-sm text-slate-500">5 Feb 2028, 2:00pm</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-2 h-2 rounded-full bg-slate-400 mt-2" />
                  <div>
                    <p className="font-medium">Order Placed</p>
                    <p className="text-sm text-slate-500">12 Sep 2026, 3:45pm by Mrs Tan Mei Ling</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
